import { CheckCircleIcon, ExclamationCircleIcon, InformationCircleIcon, XCircleIcon } from "@heroicons/react/24/outline";
import { createTheme, PaletteMode } from "@mui/material/styles";
import { CheckCircle, Circle } from "@phosphor-icons/react";

export const createAppTheme = (mode: PaletteMode) => createTheme({
    typography: {
        fontFamily: 'Outfit, sans-serif',
    },
    shape: {
        borderRadius: 16
    },
    //spacing: 12,
    components: {
        MuiCssBaseline: {
            styleOverrides: {
                '*::-webkit-scrollbar': {
                    width: '4px',
                    height: '4px',
                    display: 'none',
                },
                '*::-webkit-scrollbar-track': {
                    background: mode === 'light' ? '#fafafa' : '#292929',
                    borderRadius: '4px',
                },
                '*::-webkit-scrollbar-thumb': {
                    background: mode === 'light' ? 'rgba(0, 0, 0, 0.2)' : '#555',
                    borderRadius: '4px',
                    '&:hover': {
                        background: mode === 'light' ? 'rgba(0, 0, 0, 0.3)' : '#777',
                    },
                },
                '.react-modal-sheet-container': {
                    backgroundColor: mode === 'light' ? '#ffffff !important' : '#181818 !important',
                },
                '*': {
                    scrollbarWidth: 'thin',
                    scrollbarColor: mode === 'light'
                        ? 'rgba(0, 0, 0, 0.2) #fafafa'
                        : '#555 #292929',
                },
                '.card-spotlight': {
                    position: 'relative',
                    borderRadius: '1.5rem',
                    backgroundColor: mode === 'light' ? '#F5F5F5' : '#292929',
                    padding: '2rem',
                    overflow: 'hidden',
                    '--mouse-x': '50%',
                    '--mouse-y': '50%',
                    '--spotlight-color': mode === 'light' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
                },
            },
        },
        MuiSwipeableDrawer: {
            defaultProps: {
                elevation: 0,
                PaperProps: {
                    sx: {
                        backgroundColor: mode === 'light' ? '#ffffff' : '#181818',
                        width: '100%',
                    },
                },
            },
        },
        MuiTextField: {
            defaultProps: {
                size: 'medium',
            },
        },
        MuiAvatar: {
            styleOverrides: {
                root: {
                },
            },
        },
        MuiAppBar: {
            defaultProps: {
                elevation: 0,
            },
            styleOverrides: {
                root: {
                    borderBottom: mode === 'light' ? '0px solid rgba(0, 0, 0, 0.12)' : '0px solid rgba(255, 255, 255, 0.12)',
                    backgroundColor: mode === 'light' ? 'transparent' : 'transparent',
                    backdropFilter: 'blur(30px)',
                    WebkitBackdropFilter: 'blur(30px)',
                },
            },
        },
        MuiDialogContent: {
            styleOverrides: {
                root: {
                    paddingTop: "24px !important",
                }
            },
        },
        MuiCheckbox: {
            defaultProps: {
                checkedIcon: <CheckCircle weight="fill" size={24} />,
                icon: <Circle size={24} />,
            }
        },
        MuiCircularProgress: {
            defaultProps: {
                thickness: 2,
            },
        },
        MuiTooltip: {
            defaultProps: {
                arrow: true
            },
        },
        MuiAlert: {
            defaultProps: {
                variant: 'filled',
                iconMapping: {
                    success: <CheckCircleIcon className="size24" />,
                    error: <XCircleIcon className="size24" />,
                    warning: <ExclamationCircleIcon className="size24" />,
                    info: <InformationCircleIcon className="size24" />,
                },
            },
        },
        MuiPopover: {
            defaultProps: {
                slotProps: {
                    paper: {
                        elevation: 0,
                        sx: {
                            border: mode === 'light' ? '2px solid rgba(0, 0, 0, 0.12)' : '2px solid rgba(255, 255, 255, 0.12)',
                            backgroundColor: mode === 'light' ? 'transparent' : 'transparent',
                            backdropFilter: 'blur(30px)',
                            WebkitBackdropFilter: 'blur(30px)',

                        }
                    },
                },
            },
        },
        MuiCard: {
            styleOverrides: {
                root: {
                    border: "none",
                    //   border: mode === 'light' ? '1px solid rgba(0, 0, 0, 0.12)' : '1px solid rgba(255, 255, 255, 0.12)',
                },
            },
        },
        MuiButton: {
            defaultProps: {
                size: 'large',
            },
            styleOverrides: {
                root: {
                    boxShadow: 'none',
                    fontWeight: 400,
                    textTransform: 'none',
                    '&:hover': {
                        boxShadow: 'none',
                    },
                },
            },
        },
    },
    palette: {
        mode,
        primary: {
            main: mode === 'light' ? '#171717' : '#fff',
            light: mode === 'light' ? '#333333' : '#fff',
            dark: mode === 'light' ? '#0A0A0A' : '#fff',
            contrastText: mode === 'light' ? '#fff' : '#171717',
        },
        secondary: {
            main: '#21B0C0',
            light: '#B9EDF3',
            dark: '#0F5057',
            contrastText: '#171717',
        },
        text: {
            primary: mode === 'light' ? '#171717' : '#fff',
            disabled: mode === 'light' ? '#171717' : '#fff',
        },
        background: {
            default: mode === 'light' ? '#ffffff' : '#181818',
            paper: mode === 'light' ? '#F5F5F5' : '#292929',
        },
        error: {
            main: '#DC000B',
            light: '#FF1F2A',
            dark: '#8F0007',
            contrastText: '#fff',
        },
        info: {
            main: '#0088FF',
            light: '#5CB3FF',
            dark: '#00417A',
            contrastText: '#fff',
        },
        success: {
            main: "#00E01A",
            light: '#33FF4B',
            dark: '#00B815',
            contrastText: '#171717',
        },
        warning: {
            main: '#FFCA0A',
            light: '#FFE485',
            dark: '#B89000',
            contrastText: '#171717',
        }
    },
});